import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';

interface GoogleReview {
  author_name: string;
  author_url?: string;
  language?: string;
  profile_photo_url?: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
}

interface GooglePlaceResult {
  rating: number;
  user_ratings_total: number;
  reviews: GoogleReview[];
}

export default function GoogleReviews() {
  const [reviews, setReviews] = useState<GoogleReview[]>([]);
  const [averageRating, setAverageRating] = useState<number>(0);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGoogleReviews();
  }, []);

  const fetchGoogleReviews = async () => {
    try {
      // Google Places API endpoint
      // Para usar em produção, você precisará configurar:
      // 1. Uma API Key do Google Cloud Platform com Places API habilitada
      // 2. Um backend proxy para proteger sua API key (nunca exponha a key no frontend)
      // 3. O Place ID do seu negócio no Google

      const PLACE_ID = import.meta.env.VITE_GOOGLE_PLACE_ID || '';
      const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || '';

      if (!PLACE_ID || !API_KEY) {
        // Se não houver API key configurada, usar dados mockados
        useMockData();
        return;
      }

      // Em produção, essa requisição deve ser feita através de um backend proxy
      // para proteger sua API key
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/place/details/json?place_id=${PLACE_ID}&fields=name,rating,reviews,user_ratings_total&key=${API_KEY}`
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar avaliações');
      }

      const data = await response.json();

      if (data.status === 'OK' && data.result) {
        const result: GooglePlaceResult = data.result;
        setReviews(result.reviews || []);
        setAverageRating(result.rating || 0);
        setTotalReviews(result.user_ratings_total || 0);
      } else {
        throw new Error('Dados inválidos da API');
      }
    } catch (err) {
      console.error('Erro ao carregar avaliações do Google:', err);
      setError('Não foi possível carregar as avaliações');
      // Fallback para dados mockados em caso de erro
      useMockData();
    } finally {
      setLoading(false);
    }
  };

  const useMockData = () => {
    // Dados de exemplo enquanto a API não está configurada
    setAverageRating(4.8);
    setTotalReviews(127);
    setReviews([
      {
        author_name: 'Maria Silva',
        rating: 5,
        relative_time_description: 'há 2 semanas',
        text: 'Excelente atendimento! A equipe da Siga foi muito profissional e me ajudou a encontrar o imóvel perfeito. Recomendo!',
        time: Date.now() / 1000,
      },
      {
        author_name: 'João Santos',
        rating: 5,
        relative_time_description: 'há 1 mês',
        text: 'Muito satisfeito com o serviço. Processo de compra foi rápido e transparente. Corretores muito atenciosos.',
        time: Date.now() / 1000 - 2592000,
      },
      {
        author_name: 'Ana Costa',
        rating: 4,
        relative_time_description: 'há 3 semanas',
        text: 'Ótima imobiliária! Conseguiram vender meu apartamento rapidamente e por um bom preço.',
        time: Date.now() / 1000 - 1814400,
      },
    ]);
    setLoading(false);
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-400'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <p className="text-gray-400">Carregando avaliações...</p>
      </div>
    );
  }

  return (
    <div className="py-8 border-t border-gray-700">
      <div className="mb-6 text-center">
        <h3 className="text-xl font-semibold mb-2">Avaliações dos Clientes</h3>
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="flex items-center gap-2">
            {renderStars(Math.round(averageRating))}
            <span className="text-2xl font-bold text-yellow-400">
              {averageRating.toFixed(1)}
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-400">
          Baseado em {totalReviews} avaliações no Google
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {reviews.slice(0, 3).map((review, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-forest-800 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-3">
              {review.profile_photo_url ? (
                <img
                  src={review.profile_photo_url}
                  alt={review.author_name}
                  className="w-12 h-12 rounded-full"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-jungle-600 flex items-center justify-center text-white font-semibold">
                  {review.author_name.charAt(0)}
                </div>
              )}
              <div className="flex-1">
                <h4 className="font-semibold text-white mb-1">
                  {review.author_name}
                </h4>
                <div className="flex items-center gap-2 mb-1">
                  {renderStars(review.rating)}
                </div>
                <p className="text-xs text-gray-400">
                  {review.relative_time_description}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed line-clamp-4">
              {review.text}
            </p>
          </motion.div>
        ))}
      </div>

      {reviews.length > 0 && (
        <div className="text-center mt-6">
          <a
            href="https://www.google.com/search?q=siga+imoveis+sob+medida"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-jungle-400 hover:text-jungle-300 transition-colors text-sm font-medium"
          >
            Ver todas as avaliações no Google
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      )}

      {error && (
        <p className="text-center text-red-400 text-sm mt-4">{error}</p>
      )}
    </div>
  );
}
